/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, useEffect, useState, type CSSProperties, type FC } from 'react';

// eslint-disable-next-line import/no-cycle
import FieldLabel from '../common/FieldLabel';
import Options from './Options';
import { RadioStyled } from './styled';
import type { ControlProps, Target, Option } from '../../index';
import { isInt, notEmpty } from '../../helpers/utils';
import { sumClasses } from '../../../../utils/misc';

export interface RadioProps extends ControlProps {
  value: any;
  className?: string;
  style?: CSSProperties;
  label?: any;
  hideRadio?: boolean;
  options: Option[];
  onUpdate: (e: Target) => Promise<void>;
  default: any;
  name: string;
  iconOnTheRight?: boolean;
  columns?: number;
}

const areEqual = (prevProps: RadioProps, nextProps: RadioProps) => {
  if (prevProps.value !== nextProps.value) return false;
  if (prevProps.options !== nextProps.options) return false;
  return true;
};

const Radio: FC<RadioProps> = memo((props) => {
  const { className, style, label, name, hideRadio, options, disabled, onUpdate, iconOnTheRight, columns } = props;
  const currentIndex = options.findIndex((o: any) => o.value === props.value);
  const labelId = `radio_label_${name}`;

  const convertValue = (value: any) => {
    if (notEmpty(value)) {
      if (value.toString() === 'true') return true;
      if (value.toString() === 'false') return false;
      return isInt(value) ? parseInt(value, 10) : value;
    }
    return props.default;
  };

  const [value, setValue] = useState(convertValue(props.value));

  useEffect(() => {
    if (value !== props.value) {
      setValue(props.value);
    }
  }, [props.value]);

  const updateValue = (val: any) => {
    if (val !== props.value && !disabled) {
      const convertedValue = convertValue(val);
      setValue(convertedValue);
      onUpdate({ target: { name, value: convertedValue } });
    }
  };

  return (
    <RadioStyled
      className={sumClasses([
        'container-field oss-ui-radio',
        'radio',
        columns === 2 ? 'two-columns' : '',
        iconOnTheRight ? 'icon-on-the-right' : '',
        className || '',
      ])}
      style={style}
      role="radiogroup"
      disabled={disabled}
      {...{ 'aria-labelledby': labelId }}
    >
      <FieldLabel label={label} name={name} labelId={labelId} />
      <Options
        name={name}
        options={options}
        value={value}
        hideRadio={hideRadio}
        currentIndex={currentIndex}
        disabled={disabled}
        updateValue={updateValue}
      />
    </RadioStyled>
  );
}, areEqual);

Radio.displayName = 'Radio';

export default Radio;
